import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { IResponse } from '@shared/models';
import { Observable, of, Subject, switchMap, tap } from 'rxjs';
import { showBackendToast } from '@shared/operators/showBackendToast';
import { HttpClient } from '@angular/common/http';
import { ToastService } from '@shared/services/ui';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private readonly http = inject(HttpClient);
  private readonly toastService = inject(ToastService);
  private readonly url = environment.apiBaseUrl + 'api/files';

  refresher$ = new Subject<void>();

  filesCache = signal(new Map<string, string>());
  filesLoading = signal(new Map<string, boolean>());

  getFileSize(fileUniqueName: string) {
    return this.http.get<IResponse<number>>(`${this.url}/${fileUniqueName}/size`);
  }

  download(
    fileUniqueName: string,
    config?: {
      withSave: boolean;
      fileName: string;
    },
  ) {
    this.filesLoading.update((prev) => {
      prev.set(fileUniqueName, true);
      return prev;
    });
    return this.http
      .get<Blob>(`${this.url}/${fileUniqueName}`, {
        observe: 'response',
        responseType: 'blob' as 'json',
      })
      .pipe(
        tap((resp) => {
          this.filesCache.update((prev) => {
            prev.set(fileUniqueName, this.getUrlOfBlob(resp.body!));
            return prev;
          });
          this.filesLoading.update((prev) => {
            prev.set(fileUniqueName, false);
            return prev;
          });
          if (config?.withSave && resp.body) {
            this.save(resp.body, config.fileName);
          }
          this.refresher$.next();
        }),
      );
  }

  upload(file: File, showToast: boolean = true): Observable<IResponse<string>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<IResponse<string>>(this.url, formData, {
        observe: 'response',
      })
      .pipe(
        switchMap((resp) => {
          if (resp.body) {
            if (showToast) {
              return of(resp.body).pipe(showBackendToast(this.toastService));
            }
            return of(resp.body);
          }
          return of(resp);
        })
      );
  }

  delete(fileName: string) {
    return this.http
      .delete<IResponse<unknown>>(`${this.url}/${fileName}`)
      .pipe(showBackendToast(this.toastService));
  }

  private getUrlOfBlob(blob: Blob) {
    const urlCreator = window.URL || window.webkitURL;
    return urlCreator.createObjectURL(blob);
  }

  private save(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    document.body.appendChild(link);
    link.download = fileName;
    link.href = url;
    link.click();
    document.body.removeChild(link);
  }
}
